import { computed, ref, watch } from 'vue';
import { useMediaQuery, useTimeoutFn } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { SHELL_BREAKPOINT } from 'dashboard/constants/breakpoints';

export const SIDEBAR_MODE = { FULL: 'full', MINI: 'mini' };

// The shell is two columns: a fixed icon rail and a menu panel beside it.
export const RAIL_WIDTH = 80;
export const PANEL_WIDTH = 240;

// How many links the active section's panel would render. The panel reports it;
// the shell reads it so the spacer and the header toggle agree with what is
// actually there. Starts at 2 so an ordinary section never flashes rail-only on
// the first frame.
const panelLinkCount = ref(2);

export const setPanelLinkCount = count => {
  panelLinkCount.value = count;
};

// Grace periods for the hover hand-off. Leaving the rail waits longer because
// the cursor has to cross the rail's edge to reach the panel; leaving the panel
// only has to outlast a flick past its border.
const PEEK_CLOSE_DELAY = 200;
export const PANEL_PEEK_CLOSE_DELAY = 100;

// The peek is transient: it is never written to `sidebar_mode`, so the pinned
// state the toggle stores survives it untouched. Module scope, because the rail
// opens it and the panel keeps it open.
const isPeeking = ref(false);
const peekCloseDelay = ref(PEEK_CLOSE_DELAY);

const { start: schedulePeekClose, stop: cancelPeekClose } = useTimeoutFn(
  () => {
    isPeeking.value = false;
  },
  peekCloseDelay,
  { immediate: false }
);

/**
 * FULL or MINI, and nothing in between.
 *
 * The header toggle is the only thing that pins the panel. Pointing at the rail
 * while MINI is pinned peeks the panel open over the content - it overlays
 * rather than reflows, so nothing under the cursor moves - and it slides back
 * out on its own once the pointer leaves.
 */
export function useSidebarMode() {
  const { uiSettings, updateUISettings } = useUISettings();

  const isDesktop = useMediaQuery(`(min-width: ${SHELL_BREAKPOINT}px)`);

  // A tablet in landscape is past the shell breakpoint but has no pointer to
  // leave with: the tap synthesises `mouseenter` and nothing ever fires
  // `mouseleave`, so the peek would sit over the content for good.
  const hasHover = useMediaQuery('(hover: hover) and (pointer: fine)');

  const isMini = computed(
    () => uiSettings.value.sidebar_mode === SIDEBAR_MODE.MINI
  );

  // A section with a single link has nothing to put in a panel, so
  // it renders rail-only. This overrides the mode for that route without
  // touching the stored preference - leaving a section restores it.
  const hasPanel = computed(() => panelLinkCount.value > 1);

  // Only a pinned-shut desktop panel has anything to peek at: below the shell
  // breakpoint the panel is the off-canvas drawer, which a pointer must not open.
  const canPeek = computed(
    () => hasPanel.value && isMini.value && isDesktop.value && hasHover.value
  );

  const isPeekOpen = computed(() => canPeek.value && isPeeking.value);

  const isPanelOpen = computed(
    () => hasPanel.value && (!isMini.value || isPeekOpen.value)
  );

  const openPeek = () => {
    if (!canPeek.value) return;
    cancelPeekClose();
    isPeeking.value = true;
  };

  const closePeek = (delay = PEEK_CLOSE_DELAY) => {
    peekCloseDelay.value = delay;
    schedulePeekClose();
  };

  // `isPeeking` outlives any one component, so the rail has to put it back on
  // the way out. Tearing the shell down while peeking - a logout, say - would
  // otherwise leave the panel raised on the first frame of the next mount.
  const resetPeek = () => {
    cancelPeekClose();
    isPeeking.value = false;
  };

  // Pinning the panel open, or dropping below the shell breakpoint, retires the
  // peek outright - otherwise it would still be raised the next time the
  // conditions came back.
  watch(canPeek, value => {
    if (!value) resetPeek();
  });

  const toggleMode = () => {
    updateUISettings({
      sidebar_mode: isMini.value ? SIDEBAR_MODE.FULL : SIDEBAR_MODE.MINI,
    });
  };

  return {
    isMini,
    isDesktop,
    hasPanel,
    isPanelOpen,
    isPeekOpen,
    openPeek,
    closePeek,
    resetPeek,
    toggleMode,
  };
}
